// src/pages/settings/components/ClientSettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Import client services
import * as clientService from '../../../services/clientService';

// Import stores
import useNotificationStore from '../../../stores/notificationStore';

const ClientSettings = () => {
  const theme = useTheme();
  const { showSuccess, showError } = useNotificationStore();

  // Clients state
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Client settings form state
  const [clientSettings, setClientSettings] = useState({
    general: {
      timezone: 'UTC',
      language: 'en',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      currency: 'USD',
    },
    workflow: {
      approvalRequired: true,
      approvalLevels: 1,
      defaultApprovers: [],
    },
    notifications: {
      enableEmailNotifications: true,
      enableSmsNotifications: false,
      reportFrequency: 'monthly',
    },
    content: {
      defaultPostingFrequency: 'weekly',
      customPostingDays: [],
      postingTimeSlots: [],
      hashtagLimit: 5,
    },
    billing: {
      billingCycle: 'monthly',
      autoRenew: true,
      paymentTerms: 15,
      invoiceReminders: true,
    },
  });

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch clients from API
  const fetchClients = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await clientService.getClients();
      setClients(response.data.clients || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch clients');
      showError('Failed to fetch clients');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch client settings
  const fetchClientSettings = async (clientId) => {
    try {
      // In a real app, this would fetch the client's settings
      // For demo purposes, we're using static data
      setSelectedClient(clients.find(client => client._id === clientId));
      
      // Simulating API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      showSuccess('Client settings loaded');
    } catch (error) {
      showError('Failed to load client settings');
    }
  };

  // Open settings dialog for a client
  const handleOpenSettings = (client) => {
    setSelectedClient(client);
    fetchClientSettings(client._id);
    setOpenDialog(true);
  };

  // Close settings dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedClient(null);
  };

  // Handle changes to client settings form
  const handleSettingChange = (category, setting, value) => {
    setClientSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value,
      },
    }));
  };

  // Save client settings
  const handleSaveSettings = async () => {
    try {
      // In a real app, this would save the client settings
      // For demo purposes, we're just showing a success message
      
      // Simulating API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showSuccess('Client settings saved successfully');
      handleCloseDialog();
    } catch (error) {
      showError('Failed to save client settings');
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Filter clients based on search query
  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const paginatedClients = filteredClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ mr: 1 }} />
          Client Settings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchClients}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Search and Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search clients..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
        />
      </Box>
      
      {/* Error message */}
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      
      {/* Clients Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client Name</TableCell>
              <TableCell>Industry</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : paginatedClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    {searchQuery ? 'No clients match your search' : 'No clients found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedClients.map((client) => (
                <TableRow key={client._id}>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>{client.industry}</TableCell>
                  <TableCell>
                    <Chip 
                      label={client.status} 
                      color={
                        client.status === 'active' ? 'success' :
                        client.status === 'inactive' ? 'error' :
                        client.status === 'onboarding' ? 'warning' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      
                      onClick={() => handleOpenSettings(client)}
                      title="Edit client settings"
                    >
                      <SettingsIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredClients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      
      {/* Client Settings Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedClient ? `Settings for ${selectedClient.name}` : 'Client Settings'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedClient && (
            <Grid container spacing={3}>
              {/* General Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  General Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={clientSettings.general.timezone}
                        onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                        label="Timezone"
                      >
                        <MenuItem value="UTC">UTC</MenuItem>
                        <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                        <MenuItem value="Europe/London">London (GMT)</MenuItem>
                        <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
                        <MenuItem value="Africa/Cairo">Cairo (EET)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={clientSettings.general.language}
                        onChange={(e) => handleSettingChange('general', 'language', e.target.value)}
                        label="Language"
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="ar">Arabic</MenuItem>
                        <MenuItem value="bilingual">Bilingual (English & Arabic)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Date Format</InputLabel>
                      <Select
                        value={clientSettings.general.dateFormat}
                        onChange={(e) => handleSettingChange('general', 'dateFormat', e.target.value)}
                        label="Date Format"
                      >
                        <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                        <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                        <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Time Format</InputLabel>
                      <Select
                        value={clientSettings.general.timeFormat}
                        onChange={(e) => handleSettingChange('general', 'timeFormat', e.target.value)}
                        label="Time Format"
                      >
                        <MenuItem value="12h">12-hour (AM/PM)</MenuItem>
                        <MenuItem value="24h">24-hour</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={clientSettings.general.currency}
                        onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
                        label="Currency"
                      >
                        <MenuItem value="USD">US Dollar ($)</MenuItem>
                        <MenuItem value="EGP">Egyptian Pound (EGP)</MenuItem>
                        <MenuItem value="SAR">Saudi Riyal (SAR)</MenuItem>
                        <MenuItem value="AED">UAE Dirham (AED)</MenuItem>
                        <MenuItem value="QAR">Qatari Riyal (QAR)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Workflow Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  Workflow Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Content Approval Required</InputLabel>
                      <Select
                        value={clientSettings.workflow.approvalRequired ? 'yes' : 'no'}
                        onChange={(e) => handleSettingChange('workflow', 'approvalRequired', e.target.value === 'yes')}
                        label="Content Approval Required"
                      >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Approval Levels</InputLabel>
                      <Select
                        value={clientSettings.workflow.approvalLevels}
                        onChange={(e) => handleSettingChange('workflow', 'approvalLevels', Number(e.target.value))}
                        label="Approval Levels"
                        disabled={!clientSettings.workflow.approvalRequired}
                      >
                        <MenuItem value={1}>Single Level</MenuItem>
                        <MenuItem value={2}>Two Levels</MenuItem>
                        <MenuItem value={3}>Three Levels</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Notification Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  Notification Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Email Notifications</InputLabel>
                      <Select
                        value={clientSettings.notifications.enableEmailNotifications ? 'yes' : 'no'}
                        onChange={(e) => handleSettingChange('notifications', 'enableEmailNotifications', e.target.value === 'yes')}
                        label="Email Notifications"
                      >
                        <MenuItem value="yes">Enabled</MenuItem>
                        <MenuItem value="no">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>SMS Notifications</InputLabel>
                      <Select
                        value={clientSettings.notifications.enableSmsNotifications ? 'yes' : 'no'}
                        onChange={(e) => handleSettingChange('notifications', 'enableSmsNotifications', e.target.value === 'yes')}
                        label="SMS Notifications"
                      >
                        <MenuItem value="yes">Enabled</MenuItem>
                        <MenuItem value="no">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Report Frequency</InputLabel>
                      <Select
                        value={clientSettings.notifications.reportFrequency}
                        onChange={(e) => handleSettingChange('notifications', 'reportFrequency', e.target.value)}
                        label="Report Frequency"
                      >
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="biweekly">Bi-Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="quarterly">Quarterly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Content Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  Content Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Default Posting Frequency</InputLabel>
                      <Select
                        value={clientSettings.content.defaultPostingFrequency}
                        onChange={(e) => handleSettingChange('content', 'defaultPostingFrequency', e.target.value)}
                        label="Default Posting Frequency"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="biweekly">Bi-Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hashtag Limit"
                      type="number"
                      value={clientSettings.content.hashtagLimit}
                      onChange={(e) => handleSettingChange('content', 'hashtagLimit', Number(e.target.value))}
                      margin="normal"
                      InputProps={{ inputProps: { min: 0, max: 30 } }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Billing Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  Billing Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Billing Cycle</InputLabel>
                      <Select
                        value={clientSettings.billing.billingCycle}
                        onChange={(e) => handleSettingChange('billing', 'billingCycle', e.target.value)}
                        label="Billing Cycle"
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="quarterly">Quarterly</MenuItem>
                        <MenuItem value="annually">Annually</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Auto-Renew</InputLabel>
                      <Select
                        value={clientSettings.billing.autoRenew ? 'yes' : 'no'}
                        onChange={(e) => handleSettingChange('billing', 'autoRenew', e.target.value === 'yes')}
                        label="Auto-Renew"
                      >
                        <MenuItem value="yes">Enabled</MenuItem>
                        <MenuItem value="no">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Payment Terms (days)"
                      type="number"
                      value={clientSettings.billing.paymentTerms}
                      onChange={(e) => handleSettingChange('billing', 'paymentTerms', Number(e.target.value))}
                      margin="normal"
                      InputProps={{ inputProps: { min: 0, max: 90 } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Invoice Reminders</InputLabel>
                      <Select
                        value={clientSettings.billing.invoiceReminders ? 'yes' : 'no'}
                        onChange={(e) => handleSettingChange('billing', 'invoiceReminders', e.target.value === 'yes')}
                        label="Invoice Reminders"
                      >
                        <MenuItem value="yes">Enabled</MenuItem>
                        <MenuItem value="no">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveSettings}
            startIcon={<SaveIcon />}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientSettings;